import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';

// ===== INTERFACES =====
interface WarehouseItem { id: number; name: string; address?: string; manager?: { id: number; full_name: string }; is_active: boolean; }
interface Location { id: number; warehouse_id: number; code: string; name?: string; }
interface Product { id: number; name: string; code: string; unit?: string; }
interface Movement {
  id: number; product?: { id: number; name: string; code: string };
  warehouse_id: number; location_id?: number;
  movement_type: string; quantity: number; unit_cost?: number;
  reference?: string; note?: string;
  creator?: { id: number; full_name: string };
  movement_date: string;
}
interface Equipment {
  id: number; serial_number: string;
  product?: { id: number; name: string; code: string };
  warehouse_id?: number; location_id?: number;
  status: string; client?: { id: number; company_name?: string };
  purchase_date?: string; warranty_until?: string; notes?: string;
}
interface ChemicalBatch {
  id: number; product?: { id: number; name: string; code: string };
  warehouse_id?: number; location_id?: number;
  lot_number: string; expiry_date?: string; quantity: number; unit?: string;
  is_expired: boolean; notes?: string;
}
interface StockSummary {
  product_id: number; product_name: string; product_code: string;
  warehouse_id: number; warehouse_name: string;
  total_in: number; total_out: number; balance: number;
}
interface AssignableUser { id: number; full_name: string; role: string; role_label?: string; }

// ===== CONFIGS =====
const movementTypeConfig: Record<string, { label: string; color: string; icon: string }> = {
  incoming:   { label: 'Kirim',     color: 'bg-green-100 text-green-700',  icon: '⬇️' },
  outgoing:   { label: 'Chiqim',    color: 'bg-red-100 text-red-700',      icon: '⬆️' },
  transfer:   { label: "Ko'chirish", color: 'bg-blue-100 text-blue-700',   icon: '↔️' },
  adjustment: { label: 'Tuzatish',  color: 'bg-yellow-100 text-yellow-700', icon: '⚙️' },
};
const equipmentStatusConfig: Record<string, { label: string; color: string }> = {
  in_stock:  { label: 'Omborda',     color: 'bg-green-100 text-green-700' },
  reserved:  { label: 'Band',        color: 'bg-yellow-100 text-yellow-700' },
  sold:      { label: 'Sotilgan',    color: 'bg-gray-100 text-gray-600' },
  installed: { label: "O'rnatilgan", color: 'bg-blue-100 text-blue-700' },
  service:   { label: 'Servisda',    color: 'bg-orange-100 text-orange-700' },
};

type Tab = 'stock' | 'movements' | 'equipment' | 'chemicals';

const Warehouse: React.FC = () => {
  const { canEdit } = usePermissions();
  const canFull = canEdit('warehouse');

  const [tab, setTab] = useState<Tab>('stock');
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | ''>('');

  // Stock
  const [stockSummary, setStockSummary] = useState<StockSummary[]>([]);
  // Movements
  const [movements, setMovements] = useState<Movement[]>([]);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementForm, setMovementForm] = useState({ product_id: '', warehouse_id: '', location_id: '', movement_type: 'incoming', quantity: '', unit_cost: '', note: '', reference: '' });
  // Equipment
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [showEquipModal, setShowEquipModal] = useState(false);
  const [editEquipId, setEditEquipId] = useState<number | null>(null);
  const [equipForm, setEquipForm] = useState({ serial_number: '', product_id: '', warehouse_id: '', location_id: '', status: 'in_stock', purchase_date: '', warranty_until: '', notes: '' });
  // Chemicals
  const [chemicals, setChemicals] = useState<ChemicalBatch[]>([]);
  const [showChemModal, setShowChemModal] = useState(false);
  const [editChemId, setEditChemId] = useState<number | null>(null);
  const [chemForm, setChemForm] = useState({ product_id: '', warehouse_id: '', location_id: '', lot_number: '', expiry_date: '', quantity: '', unit: '', notes: '' });
  // Warehouse management
  const [showWHModal, setShowWHModal] = useState(false);
  const [editWHId, setEditWHId] = useState<number | null>(null);
  const [whForm, setWhForm] = useState({ name: '', address: '', manager_id: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleWarehouseReport = async (type: string, format: string) => {
    try {
      if (format === 'excel') {
        const res = await api.get('/warehouse-report/excel', { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a'); a.href = url;
        a.download = 'ombor_hisobot.xlsx'; a.click();
        URL.revokeObjectURL(url);
      } else {
        const res = await api.get('/warehouse-report/pdf', {
          params: { report_type: type },
          responseType: 'blob'
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a'); a.href = url;
        a.download = `ombor_hisobot_${type}.pdf`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch { alert('Hisobot yaratishda xato'); }
  };

  const fetchAll = useCallback(async () => {
    try { const r = await api.get('/warehouses'); setWarehouses(r.data); } catch {}
    try { const r = await api.get('/warehouse-stock'); setStockSummary(r.data); } catch {}
    try { const r = await api.get('/warehouse-movements', { params: { limit: 50 } }); setMovements(r.data); } catch {}
    try { const r = await api.get('/equipment'); setEquipment(r.data); } catch {}
    try { const r = await api.get('/chemical-batches'); setChemicals(r.data); } catch {}
    try { const r = await api.get('/products'); setProducts(r.data); } catch {}
    try { const r = await api.get('/users/assignable'); setAssignableUsers(r.data); } catch {}
  }, []);

  const fetchLocations = useCallback(async (wid: number) => {
    try { const r = await api.get(`/warehouses/${wid}/locations`); setLocations(prev => [...prev.filter(l => l.warehouse_id !== wid), ...r.data]); } catch {}
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (selectedWarehouse) fetchLocations(Number(selectedWarehouse));
  }, [selectedWarehouse, fetchLocations]);

  // ===== MOVEMENT SUBMIT =====
  const handleMovementSubmit = async () => {
    if (!movementForm.product_id || !movementForm.warehouse_id || !movementForm.movement_type || !movementForm.quantity) { setError("Mahsulot, omborxona va miqdor majburiy"); return; }
    setLoading(true); setError('');
    try {
      await api.post('/warehouse-movements', {
        ...movementForm,
        product_id: Number(movementForm.product_id),
        warehouse_id: Number(movementForm.warehouse_id),
        location_id: movementForm.location_id ? Number(movementForm.location_id) : null,
        quantity: Number(movementForm.quantity),
        unit_cost: movementForm.unit_cost ? Number(movementForm.unit_cost) : null,
      });
      setShowMovementModal(false);
      setMovementForm({ product_id: '', warehouse_id: '', location_id: '', movement_type: 'incoming', quantity: '', unit_cost: '', note: '', reference: '' });
      await fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  // ===== EQUIPMENT SUBMIT =====
  const handleEquipSubmit = async () => {
    if (!equipForm.serial_number || !equipForm.product_id) { setError("Serial raqam va mahsulot majburiy"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...equipForm,
        product_id: Number(equipForm.product_id),
        warehouse_id: equipForm.warehouse_id ? Number(equipForm.warehouse_id) : null,
        location_id: equipForm.location_id ? Number(equipForm.location_id) : null,
        purchase_date: equipForm.purchase_date || null,
        warranty_until: equipForm.warranty_until || null,
      };
      if (editEquipId) await api.put(`/equipment/${editEquipId}`, payload);
      else await api.post('/equipment', payload);
      setShowEquipModal(false); setEditEquipId(null);
      setEquipForm({ serial_number: '', product_id: '', warehouse_id: '', location_id: '', status: 'in_stock', purchase_date: '', warranty_until: '', notes: '' });
      await fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  // ===== CHEMICAL SUBMIT =====
  const handleChemSubmit = async () => {
    if (!chemForm.product_id || !chemForm.lot_number) { setError("Mahsulot va lot raqami majburiy"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...chemForm,
        product_id: Number(chemForm.product_id),
        warehouse_id: chemForm.warehouse_id ? Number(chemForm.warehouse_id) : null,
        location_id: chemForm.location_id ? Number(chemForm.location_id) : null,
        quantity: Number(chemForm.quantity) || 0,
        expiry_date: chemForm.expiry_date || null,
      };
      if (editChemId) await api.put(`/chemical-batches/${editChemId}`, payload);
      else await api.post('/chemical-batches', payload);
      setShowChemModal(false); setEditChemId(null);
      setChemForm({ product_id: '', warehouse_id: '', location_id: '', lot_number: '', expiry_date: '', quantity: '', unit: '', notes: '' });
      await fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  // ===== WAREHOUSE SUBMIT =====
  const handleWHSubmit = async () => {
    if (!whForm.name) { setError("Nom majburiy"); return; }
    setLoading(true); setError('');
    try {
      const payload = { ...whForm, manager_id: whForm.manager_id ? Number(whForm.manager_id) : null };
      if (editWHId) await api.put(`/warehouses/${editWHId}`, payload);
      else await api.post('/warehouses', payload);
      setShowWHModal(false); setEditWHId(null);
      setWhForm({ name: '', address: '', manager_id: '' });
      await fetchAll();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setLoading(false); }
  };

  const openEditEquip = (eq: Equipment) => {
    setEquipForm({ serial_number: eq.serial_number, product_id: eq.product?.id?.toString() || '', warehouse_id: eq.warehouse_id?.toString() || '', location_id: eq.location_id?.toString() || '', status: eq.status, purchase_date: eq.purchase_date?.split('T')[0] || '', warranty_until: eq.warranty_until?.split('T')[0] || '', notes: eq.notes || '' });
    setEditEquipId(eq.id); setShowEquipModal(true);
  };

  const openEditChem = (b: ChemicalBatch) => {
    setChemForm({ product_id: b.product?.id?.toString() || '', warehouse_id: b.warehouse_id?.toString() || '', location_id: b.location_id?.toString() || '', lot_number: b.lot_number, expiry_date: b.expiry_date || '', quantity: b.quantity.toString(), unit: b.unit || '', notes: b.notes || '' });
    setEditChemId(b.id); setShowChemModal(true);
  };

  const warehouseMap = Object.fromEntries(warehouses.map(w => [w.id, w.name]));
  const locationMap = Object.fromEntries(locations.map(l => [l.id, `${l.code}${l.name ? ' — ' + l.name : ''}`]));
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">🏪 Ombor boshqaruvi</h2>
          <p className="text-gray-500 text-xs md:text-sm mt-1 hidden sm:block">Omborxonalar, harakatlar, uskunalar, kimyoviy partiyalar</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => handleWarehouseReport('summary', 'pdf')}
            className="px-2 md:px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs md:text-sm">
            📄 <span className="hidden sm:inline">Qisqacha PDF</span>
          </button>
          <button onClick={() => handleWarehouseReport('detailed', 'pdf')}
            className="px-2 md:px-3 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-xs md:text-sm">
            📄 <span className="hidden sm:inline">Kengaytirilgan PDF</span>
          </button>
          <button onClick={() => handleWarehouseReport('all', 'excel')}
            className="px-2 md:px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs md:text-sm">
            📊 Excel
          </button>
          {canFull && (
            <button onClick={() => setShowWHModal(true)}
              className="px-2 md:px-3 py-2 bg-gray-700 text-white rounded-lg text-xs md:text-sm hover:bg-gray-800">
              🏪 <span className="hidden sm:inline">Omborxona qo'shish</span><span className="sm:hidden">Qo'shish</span>
            </button>
          )}
        </div>
      </div>

      {/* Omborxonalar kartalar */}
      {warehouses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
          {warehouses.map(w => (
            <div key={w.id} className={`bg-white rounded-xl shadow p-3 md:p-4 border-l-4 ${selectedWarehouse === w.id ? 'border-blue-500' : 'border-gray-200'} cursor-pointer hover:shadow-md transition`}
              onClick={() => { setSelectedWarehouse(selectedWarehouse === w.id ? '' : w.id); fetchLocations(w.id); }}>
              <div className="flex justify-between">
                <h3 className="font-semibold text-gray-800 text-sm md:text-base">🏪 {w.name}</h3>
                {canFull && (
                  <button onClick={e => { e.stopPropagation(); setWhForm({ name: w.name, address: w.address || '', manager_id: w.manager?.id?.toString() || '' }); setEditWHId(w.id); setShowWHModal(true); }}
                    className="text-gray-400 hover:text-blue-500 text-xs flex-shrink-0">✏️</button>
                )}
              </div>
              {w.address && <p className="text-gray-500 text-xs mt-1">📍 {w.address}</p>}
              {w.manager && <p className="text-gray-500 text-xs">👤 {w.manager.full_name}</p>}
              {selectedWarehouse === w.id && (
                <p className="text-blue-500 text-xs mt-1 font-medium">✓ Tanlangan</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tablar */}
      <div className="bg-white rounded-xl shadow">
        <div className="border-b flex overflow-x-auto">
          {([
            { key: 'stock', label: 'Qoldiqlar', icon: '📊' },
            { key: 'movements', label: 'Harakatlar', icon: '↕️' },
            { key: 'equipment', label: 'Uskunalar', icon: '🔬' },
            { key: 'chemicals', label: 'Kimyoviy partiyalar', icon: '🧪' },
          ] as { key: Tab; label: string; icon: string }[]).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 md:px-5 py-3 text-xs md:text-sm font-medium border-b-2 transition whitespace-nowrap flex-shrink-0 ${tab === t.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="p-3 md:p-5">

          {/* ===== STOCK TAB ===== */}
          {tab === 'stock' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">Mahsulot qoldiqlari</h3>
                {selectedWarehouse && <span className="text-xs md:text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-full">Filtr: {warehouseMap[selectedWarehouse]}</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-2 md:px-4 py-3 text-left">Mahsulot</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Omborxona</th>
                      <th className="px-2 md:px-4 py-3 text-right hidden sm:table-cell">Kirim</th>
                      <th className="px-2 md:px-4 py-3 text-right hidden sm:table-cell">Chiqim</th>
                      <th className="px-2 md:px-4 py-3 text-right">Qoldiq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockSummary.filter(s => !selectedWarehouse || s.warehouse_id === Number(selectedWarehouse)).length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-10 text-gray-400">Qoldiqlar yo'q</td></tr>
                    ) : stockSummary.filter(s => !selectedWarehouse || s.warehouse_id === Number(selectedWarehouse)).map((s, i) => (
                      <tr key={i} className={`border-t hover:bg-gray-50 ${s.balance <= 0 ? 'bg-red-50' : ''}`}>
                        <td className="px-2 md:px-4 py-3">
                          <div className="font-medium">{s.product_name}</div>
                          <div className="text-gray-400 font-mono text-xs">{s.product_code}</div>
                          <div className="md:hidden text-xs text-gray-400 mt-0.5">{s.warehouse_name}</div>
                          <div className="sm:hidden text-xs mt-0.5">
                            <span className="text-green-600">+{s.total_in}</span> / <span className="text-red-500">-{s.total_out}</span>
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-3 text-gray-500 hidden md:table-cell">{s.warehouse_name}</td>
                        <td className="px-2 md:px-4 py-3 text-right text-green-600 font-medium hidden sm:table-cell">{s.total_in}</td>
                        <td className="px-2 md:px-4 py-3 text-right text-red-500 hidden sm:table-cell">{s.total_out}</td>
                        <td className={`px-2 md:px-4 py-3 text-right font-bold ${s.balance <= 0 ? 'text-red-600' : 'text-gray-800'}`}>{s.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== MOVEMENTS TAB ===== */}
          {tab === 'movements' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">Ombor harakatlari</h3>
                {canFull && (
                  <button onClick={() => setShowMovementModal(true)}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs md:text-sm hover:bg-blue-700">
                    + Harakat qo'shish
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-2 md:px-4 py-3 text-left">Tur / Mahsulot</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Omborxona</th>
                      <th className="px-2 md:px-4 py-3 text-right">Miqdor</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Havola</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Izoh</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Kim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.filter(m => !selectedWarehouse || m.warehouse_id === Number(selectedWarehouse)).length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">Harakatlar yo'q</td></tr>
                    ) : movements.filter(m => !selectedWarehouse || m.warehouse_id === Number(selectedWarehouse)).map(m => (
                      <tr key={m.id} className="border-t hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${movementTypeConfig[m.movement_type]?.color}`}>
                            {movementTypeConfig[m.movement_type]?.icon} {movementTypeConfig[m.movement_type]?.label}
                          </span>
                          <div className="font-medium mt-1">{m.product?.name}<span className="text-gray-400 text-xs ml-1">({m.product?.code})</span></div>
                          <div className="md:hidden text-xs text-gray-400 mt-0.5">{warehouseMap[m.warehouse_id] || '—'} · {new Date(m.movement_date).toLocaleDateString('ru-RU')}</div>
                        </td>
                        <td className="px-2 md:px-4 py-3 text-gray-500 hidden md:table-cell">
                          {warehouseMap[m.warehouse_id] || '—'}
                          <div className="text-xs text-gray-400">{new Date(m.movement_date).toLocaleDateString('ru-RU')}</div>
                        </td>
                        <td className={`px-2 md:px-4 py-3 text-right font-bold whitespace-nowrap ${m.movement_type === 'incoming' ? 'text-green-600' : m.movement_type === 'outgoing' ? 'text-red-500' : 'text-blue-600'}`}>
                          {m.movement_type === 'incoming' ? '+' : m.movement_type === 'outgoing' ? '-' : '↔'}{m.quantity}
                        </td>
                        <td className="px-2 md:px-4 py-3 text-blue-600 text-xs font-mono hidden lg:table-cell">{m.reference || '—'}</td>
                        <td className="px-2 md:px-4 py-3 text-gray-500 text-xs max-w-xs truncate hidden lg:table-cell">{m.note || '—'}</td>
                        <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{m.creator?.full_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== EQUIPMENT TAB ===== */}
          {tab === 'equipment' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">Laboratoriya uskunalari</h3>
                {canFull && (
                  <button onClick={() => { setEditEquipId(null); setEquipForm({ serial_number: '', product_id: '', warehouse_id: '', location_id: '', status: 'in_stock', purchase_date: '', warranty_until: '', notes: '' }); setShowEquipModal(true); }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs md:text-sm hover:bg-blue-700">
                    + Uskuna qo'shish
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-2 md:px-4 py-3 text-left">Serial / Mahsulot</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Omborxona / Joy</th>
                      <th className="px-2 md:px-4 py-3 text-left">Holat</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Mijoz</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden sm:table-cell">Kafolat</th>
                      <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.filter(e => !selectedWarehouse || e.warehouse_id === Number(selectedWarehouse)).length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">Uskunalar yo'q</td></tr>
                    ) : equipment.filter(e => !selectedWarehouse || e.warehouse_id === Number(selectedWarehouse)).map(eq => {
                      const warrantyExpiring = eq.warranty_until && eq.warranty_until < new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0] && eq.warranty_until >= today;
                      const warrantyExpired = eq.warranty_until && eq.warranty_until < today;
                      return (
                        <tr key={eq.id} className={`border-t hover:bg-gray-50 ${warrantyExpired ? 'bg-red-50' : warrantyExpiring ? 'bg-yellow-50' : ''}`}>
                          <td className="px-2 md:px-4 py-3">
                            <div className="font-mono text-blue-600 font-medium text-xs">{eq.serial_number}</div>
                            <div className="font-medium">{eq.product?.name}<span className="text-gray-400 text-xs ml-1">({eq.product?.code})</span></div>
                            <div className="md:hidden text-xs text-gray-400 mt-0.5">
                              {eq.warehouse_id ? warehouseMap[eq.warehouse_id] : '—'}
                              {eq.location_id && <span className="ml-1 bg-gray-100 px-1 rounded">{locationMap[eq.location_id]}</span>}
                            </div>
                            {eq.warranty_until && (
                              <div className={`sm:hidden text-xs mt-0.5 ${warrantyExpired ? 'text-red-600 font-medium' : warrantyExpiring ? 'text-yellow-600 font-medium' : 'text-gray-400'}`}>
                                {warrantyExpired ? '⚠️ ' : warrantyExpiring ? '⏰ ' : ''}{eq.warranty_until}
                              </div>
                            )}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                            {eq.warehouse_id ? warehouseMap[eq.warehouse_id] : '—'}
                            {eq.location_id && <span className="ml-1 bg-gray-100 px-1 rounded">{locationMap[eq.location_id]}</span>}
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${equipmentStatusConfig[eq.status]?.color}`}>
                              {equipmentStatusConfig[eq.status]?.label}
                            </span>
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{eq.client?.company_name || '—'}</td>
                          <td className="px-2 md:px-4 py-3 text-xs hidden sm:table-cell">
                            {eq.warranty_until ? (
                              <span className={warrantyExpired ? 'text-red-600 font-medium' : warrantyExpiring ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                                {warrantyExpired ? '⚠️ ' : warrantyExpiring ? '⏰ ' : ''}{eq.warranty_until}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-center">
                            {canFull && (
                              <button onClick={() => openEditEquip(eq)}
                                className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">✏️</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== CHEMICALS TAB ===== */}
          {tab === 'chemicals' && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">Kimyoviy partiyalar</h3>
                {canFull && (
                  <button onClick={() => { setEditChemId(null); setChemForm({ product_id: '', warehouse_id: '', location_id: '', lot_number: '', expiry_date: '', quantity: '', unit: '', notes: '' }); setShowChemModal(true); }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs md:text-sm hover:bg-blue-700">
                    + Partiya qo'shish
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-2 md:px-4 py-3 text-left">Mahsulot / Lot</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden md:table-cell">Omborxona / Joy</th>
                      <th className="px-2 md:px-4 py-3 text-right">Miqdor</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden sm:table-cell">Yaroqlilik</th>
                      <th className="px-2 md:px-4 py-3 text-left hidden lg:table-cell">Holat</th>
                      <th className="px-2 md:px-4 py-3 text-center">Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chemicals.filter(c => !selectedWarehouse || c.warehouse_id === Number(selectedWarehouse)).length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-10 text-gray-400">Partiyalar yo'q</td></tr>
                    ) : chemicals.filter(c => !selectedWarehouse || c.warehouse_id === Number(selectedWarehouse)).map(b => {
                      const expiringSoon = b.expiry_date && !b.is_expired && b.expiry_date < new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0];
                      return (
                        <tr key={b.id} className={`border-t hover:bg-gray-50 ${b.is_expired ? 'bg-red-50' : expiringSoon ? 'bg-yellow-50' : ''}`}>
                          <td className="px-2 md:px-4 py-3">
                            <div className="font-medium">{b.product?.name}<span className="text-gray-400 text-xs ml-1">({b.product?.code})</span></div>
                            <div className="font-mono text-blue-600 text-xs">{b.lot_number}</div>
                            <div className="md:hidden text-xs text-gray-400 mt-0.5">
                              {b.warehouse_id ? warehouseMap[b.warehouse_id] : '—'}
                              {b.location_id && <span className="ml-1 bg-gray-100 px-1 rounded">{locationMap[b.location_id]}</span>}
                            </div>
                            {b.expiry_date && (
                              <div className={`sm:hidden text-xs mt-0.5 ${b.is_expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-yellow-600' : 'text-gray-400'}`}>
                                {b.is_expired ? '❌ ' : expiringSoon ? '⏰ ' : ''}{b.expiry_date}
                              </div>
                            )}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                            {b.warehouse_id ? warehouseMap[b.warehouse_id] : '—'}
                            {b.location_id && <span className="ml-1 bg-gray-100 px-1 rounded">{locationMap[b.location_id]}</span>}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-right font-medium whitespace-nowrap">{b.quantity} {b.unit || ''}</td>
                          <td className="px-2 md:px-4 py-3 text-xs hidden sm:table-cell">
                            {b.expiry_date ? (
                              <span className={b.is_expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-yellow-600' : 'text-gray-500'}>
                                {b.is_expired ? '❌ ' : expiringSoon ? '⏰ ' : ''}{b.expiry_date}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-2 md:px-4 py-3 hidden lg:table-cell">
                            {b.is_expired ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Muddati o'tgan</span>
                            ) : expiringSoon ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">Tez tugaydi</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Yaroqli</span>
                            )}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-center">
                            {canFull && (
                              <button onClick={() => openEditChem(b)}
                                className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">✏️</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== MOVEMENT MODAL ===== */}
      {showMovementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">Ombor harakati qo'shish</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mahsulot *</label>
                <select value={movementForm.product_id} onChange={e => setMovementForm({...movementForm, product_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Omborxona *</label>
                <select value={movementForm.warehouse_id} onChange={e => { setMovementForm({...movementForm, warehouse_id: e.target.value, location_id: ''}); if (e.target.value) fetchLocations(Number(e.target.value)); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joy</label>
                <select value={movementForm.location_id} onChange={e => setMovementForm({...movementForm, location_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Ixtiyoriy —</option>
                  {locations.filter(l => l.warehouse_id === Number(movementForm.warehouse_id)).map(l => <option key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tur *</label>
                <select value={movementForm.movement_type} onChange={e => setMovementForm({...movementForm, movement_type: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="incoming">⬇️ Kirim</option>
                  <option value="outgoing">⬆️ Chiqim</option>
                  <option value="transfer">↔️ Ko'chirish</option>
                  <option value="adjustment">⚙️ Tuzatish</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Miqdor *</label>
                <input type="number" min="0" value={movementForm.quantity} onChange={e => setMovementForm({...movementForm, quantity: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birlik narxi</label>
                <input type="number" min="0" value={movementForm.unit_cost} onChange={e => setMovementForm({...movementForm, unit_cost: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Havola (PR raqami)</label>
                <input value={movementForm.reference} onChange={e => setMovementForm({...movementForm, reference: e.target.value})}
                  placeholder="PR-2026-001"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={movementForm.note} onChange={e => setMovementForm({...movementForm, note: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowMovementModal(false); setError(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={handleMovementSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{loading ? '...' : 'Saqlash'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EQUIPMENT MODAL ===== */}
      {showEquipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editEquipId ? 'Uskunani tahrirlash' : 'Yangi uskuna'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serial raqam *</label>
                <input value={equipForm.serial_number} onChange={e => setEquipForm({...equipForm, serial_number: e.target.value})}
                  placeholder="SN-2026-001"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mahsulot *</label>
                <select value={equipForm.product_id} onChange={e => setEquipForm({...equipForm, product_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Omborxona</label>
                <select value={equipForm.warehouse_id} onChange={e => { setEquipForm({...equipForm, warehouse_id: e.target.value, location_id: ''}); if (e.target.value) fetchLocations(Number(e.target.value)); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joy</label>
                <select value={equipForm.location_id} onChange={e => setEquipForm({...equipForm, location_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Ixtiyoriy —</option>
                  {locations.filter(l => l.warehouse_id === Number(equipForm.warehouse_id)).map(l => <option key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holat</label>
                <select value={equipForm.status} onChange={e => setEquipForm({...equipForm, status: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(equipmentStatusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Xarid sanasi</label>
                <input type="date" value={equipForm.purchase_date} onChange={e => setEquipForm({...equipForm, purchase_date: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kafolat muddati</label>
                <input type="date" value={equipForm.warranty_until} onChange={e => setEquipForm({...equipForm, warranty_until: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={equipForm.notes} onChange={e => setEquipForm({...equipForm, notes: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowEquipModal(false); setError(''); setEditEquipId(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={handleEquipSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{loading ? '...' : 'Saqlash'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CHEMICAL MODAL ===== */}
      {showChemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editChemId ? 'Partiyani tahrirlash' : 'Yangi kimyoviy partiya'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mahsulot *</label>
                <select value={chemForm.product_id} onChange={e => setChemForm({...chemForm, product_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lot raqami *</label>
                <input value={chemForm.lot_number} onChange={e => setChemForm({...chemForm, lot_number: e.target.value})}
                  placeholder="ACN-250601"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yaroqlilik muddati</label>
                <input type="date" value={chemForm.expiry_date} onChange={e => setChemForm({...chemForm, expiry_date: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Omborxona</label>
                <select value={chemForm.warehouse_id} onChange={e => { setChemForm({...chemForm, warehouse_id: e.target.value, location_id: ''}); if (e.target.value) fetchLocations(Number(e.target.value)); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joy</label>
                <select value={chemForm.location_id} onChange={e => setChemForm({...chemForm, location_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Ixtiyoriy —</option>
                  {locations.filter(l => l.warehouse_id === Number(chemForm.warehouse_id)).map(l => <option key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Miqdor</label>
                <input type="number" min="0" value={chemForm.quantity} onChange={e => setChemForm({...chemForm, quantity: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birlik (ml, L, g, kg)</label>
                <input value={chemForm.unit} onChange={e => setChemForm({...chemForm, unit: e.target.value})}
                  placeholder="ml"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={chemForm.notes} onChange={e => setChemForm({...chemForm, notes: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowChemModal(false); setError(''); setEditChemId(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={handleChemSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{loading ? '...' : 'Saqlash'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== WAREHOUSE MODAL ===== */}
      {showWHModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editWHId ? 'Omborxonani tahrirlash' : 'Yangi omborxona'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nomi *</label>
                <input value={whForm.name} onChange={e => setWhForm({...whForm, name: e.target.value})}
                  placeholder="Main Warehouse"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manzil</label>
                <input value={whForm.address} onChange={e => setWhForm({...whForm, address: e.target.value})}
                  placeholder="Toshkent, Yunusobod..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul rahbar</label>
                <select value={whForm.manager_id} onChange={e => setWhForm({...whForm, manager_id: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role_label || u.role})</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setShowWHModal(false); setError(''); setEditWHId(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={handleWHSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">{loading ? '...' : 'Saqlash'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Warehouse;
